/**
 * Hooks de mutación de **atendidos** (alta, edición, borrado).
 *
 * Cada mutación, en éxito, **invalida las queries afectadas** (`docs/
 * frontend_conventions.md`, Datos): la raíz `['admin-atendidos']` cubre la lista
 * (de cualquier sede) y los detalles. Como el listado de sedes expone el conteo
 * de trabajadores por sede (`stats.total_workers`), también se invalida la rama
 * de sedes para que esas estadísticas reflejen el cambio. El atendido se crea/
 * edita en la sede activa (header `X-Sede-Id`, lo inyecta `lib/api`); el gating
 * de permiso (`all`) lo decide la UI; aquí solo se ejecuta y se propaga el
 * `ApiError`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAtendido, deleteAtendido, updateAtendido } from "../api";
import type {
  AtendidoDetail,
  CreateAtendidoBody,
  UpdateAtendidoBody,
} from "../types";
import { ATENDIDOS_ROOT_KEY, SEDES_ROOT_KEY } from "./queryKeys";

/** Invalida la rama de atendidos y las stats de sedes (conteo de trabajadores). */
function useInvalidateAtendidos() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ATENDIDOS_ROOT_KEY });
    void queryClient.invalidateQueries({ queryKey: SEDES_ROOT_KEY });
  };
}

/** Alta de atendido (`POST /atendidos`) en la sede activa. */
export function useCreateAtendido() {
  const invalidate = useInvalidateAtendidos();
  return useMutation<AtendidoDetail, Error, CreateAtendidoBody>({
    mutationFn: (body) => createAtendido(body),
    onSuccess: invalidate,
  });
}

/** Argumentos de edición: el id del atendido + los cambios. */
interface UpdateAtendidoArgs {
  atendidoId: number;
  body: UpdateAtendidoBody;
}

/** Edición de atendido (`PATCH /atendidos/<id>`). */
export function useUpdateAtendido() {
  const invalidate = useInvalidateAtendidos();
  return useMutation<AtendidoDetail, Error, UpdateAtendidoArgs>({
    mutationFn: ({ atendidoId, body }) => updateAtendido(atendidoId, body),
    onSuccess: invalidate,
  });
}

/** Borrado de atendido (`DELETE /atendidos/<id>`). */
export function useDeleteAtendido() {
  const invalidate = useInvalidateAtendidos();
  return useMutation<void, Error, number>({
    mutationFn: (atendidoId) => deleteAtendido(atendidoId),
    onSuccess: invalidate,
  });
}
